import { Button } from '@app/components/various/Button';
import { Option } from '@app/components/various/Fields/Select';
import { AutocompleteField, Form, FormFieldset, FormGrid, InputField } from '@app/components/various/Form';
import { StickyModalFooter } from '@app/components/various/Modal';
import { getIsOrderSettingsFormDisabled } from '@app/ducks/ui';
import { getIsSeller, getUser } from '@app/ducks/user';
import { getAccountNameWithId, getBuyerFullName } from '@app/logic/accounts';
import { useBuyerSelect } from '@app/utils/hooks/selection/useBuyerSelect';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { isDefined } from '@app/utils/is';
import { Buyer, BuyerForNewSelection } from '@typings/@centra';
import { Selects } from '@typings/components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

interface FormData {
  buyers: string[];
  selectionName: string;
}

interface SubmitData {
  buyers: BuyerForNewSelection[];
  selectionName: string;
}

interface Props {
  submitTitle: string;
  disableBuyerSelect?: boolean;
  initialData?: Partial<FormData>;
  onClose: () => void;
  onSubmit: (data: SubmitData) => void;
  isPending?: boolean;
}

export const CreateSelectionForm = ({ submitTitle, initialData, onClose, onSubmit, disableBuyerSelect, isPending }: Props) => {
  const { t } = useTranslation(['common', 'selections', 'validation', 'buyers']);
  const user = useSelector(getUser);
  const isUserSeller = useSelector(getIsSeller);
  const isFormDisabled = useSelector(getIsOrderSettingsFormDisabled);

  const {
    defaultOption,
    handleLoadMore,
    handleSearchChange,
    isLoading: isLoadingBuyers,
    options,
  } = useBuyerSelect(initialData?.buyers?.[0]);

  const [selectedBuyers, setSelectedBuyers] = React.useState<Buyer.ListElement[]>([]);
  const defaultValues = {
    buyers: isUserSeller ? [] : [user.buyer],
    selectionName: initialData?.selectionName ?? '',
  };

  const validationSchema: yup.ObjectSchema<FormData> = yup.object({
    buyers: yup.array(yup.string().defined()).defined(t('validation:select_buyer_hint')),
    selectionName: yup.string().defined(),
  });

  const formMethods = useValidatedForm({ defaultValues, validationSchema });
  const { register, setValue } = formMethods;

  React.useEffect(() => {
    if (isDefined(defaultOption)) {
      setValue('buyers', [defaultOption.id]);
    }
  }, [defaultOption, setValue]);

  const handleChange = (value: Selects.Selection) => {
    if (!isDefined(value) || !Array.isArray(value)) {
      return;
    }

    setSelectedBuyers(currentSelected => [...currentSelected, ...options.filter(buyer => value.includes(buyer.id))]);
  };

  const shouldShowBuyerSelect = isUserSeller && !disableBuyerSelect;

  const handleSubmit = (formData: FormData) => {
    const { buyers, selectionName } = formData;

    const submitData: SubmitData = {
      buyers: buyers.map(buyerId => {
        const buyerData = options.find(buyer => buyer.id === buyerId);

        return {
          buyer: buyerId,
          name: isDefined(buyerData) ? `${getBuyerFullName(buyerData)} (${getAccountNameWithId(buyerData)})` : undefined,
        };
      }),
      selectionName,
    };

    onSubmit(submitData);
  };

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit}>
      <FormFieldset isDisabled={isFormDisabled}>
        <FormGrid>
          {shouldShowBuyerSelect && (
            <AutocompleteField
              label={t('buyers:buyer_one')}
              isLoadingOptions={isLoadingBuyers}
              isRequired
              overflowContainer
              name="buyers"
              dataTestId="buyerSelect"
              placeholder={t('buyers:select_buyer')}
              renderPill={value => {
                const buyer = selectedBuyers.find(b => b.id === value);

                return `${buyer?.firstName} ${buyer?.lastName}`;
              }}
              onSearchChange={handleSearchChange}
              onLoadMore={handleLoadMore}
              onChange={handleChange}
            >
              {options.map(buyer => (
                <Option maskData key={buyer.id} value={buyer.id} description={getAccountNameWithId(buyer)}>
                  {getBuyerFullName(buyer)}
                </Option>
              ))}
            </AutocompleteField>
          )}
          <InputField
            label={t('common:title')}
            placeholder={t('selections:name_selection')}
            dataTestId="selectionNameInput"
            isRequired={false}
            {...register('selectionName')}
          />
        </FormGrid>
        <StickyModalFooter>
          <div className="flex justify-end gap-2">
            <Button color="secondary" variant="ghost" isLoading={isPending} disabled={isFormDisabled} onClick={onClose}>
              {t('common:cancel')}
            </Button>
            <Button type="submit" data-testid="createSelectionModalButton" isLoading={isFormDisabled || isPending}>
              {submitTitle}
            </Button>
          </div>
        </StickyModalFooter>
      </FormFieldset>
    </Form>
  );
};
